import { NavLink, Outlet, Navigate, Link } from "react-router-dom";
import { LayoutDashboard, CalendarRange, Users, Truck, MessageSquareQuote, ArrowLeft, FlaskConical } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogOut } from "lucide-react";
import api from "@/lib/api";
import { BrandLockup } from "@/components/BrandLogo";

const nav = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/programmes", label: "Programmes", icon: CalendarRange },
  { to: "/admin/applicants", label: "Applicants", icon: Users },
  { to: "/admin/dispatch", label: "Dispatch & trials", icon: Truck },
  { to: "/admin/stories", label: "Story moderation", icon: MessageSquareQuote },
  { to: "/admin/test-showcase", label: "Test showcase", icon: FlaskConical },
];

export default function AdminLayout() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-ivory"><p className="font-serif text-2xl text-forest animate-pulse">One moment…</p></div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-ivory lg:grid lg:grid-cols-[260px_1fr]" data-testid="admin-layout">
      <aside className="bg-forest text-ivory flex flex-col lg:min-h-screen">
        <div className="px-6 py-6 border-b border-ivory/10 flex items-center justify-between">
          <Link to="/"><BrandLockup tone="light" compact /></Link>
          <span className="text-[10px] uppercase tracking-[0.24em] text-champagne">Admin</span>
        </div>
        <nav className="flex lg:flex-col overflow-x-auto lg:overflow-visible px-3 py-4 gap-1">
          {nav.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-[11px] uppercase tracking-[0.2em] whitespace-nowrap transition-colors ${isActive ? "bg-ivory text-forest" : "text-ivory/70 hover:text-ivory hover:bg-ivory/10"}`}>
              <Icon size={15} strokeWidth={1.5} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto px-6 py-6 border-t border-ivory/10 text-xs text-ivory/50 font-light">
          <p>{user.email}</p>
          <div className="mt-3 flex flex-wrap gap-4">
            <Link to="/" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-ivory/70 hover:text-ivory"><ArrowLeft size={12} /> Site</Link>
            <button onClick={async () => { try { await api.post("/auth/logout"); } catch (e) { console.error("Logout request failed:", e); } window.location.assign("/admin/login"); }} className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-ivory/70 hover:text-ivory" data-testid="admin-logout"><LogOut size={12} /> Sign out</button>
          </div>
        </div>
      </aside>
      <main className="px-5 sm:px-8 lg:px-12 py-10 max-w-6xl w-full"><Outlet /></main>
    </div>
  );
}

export function AdminHeading({ eyebrow, title, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1 className="mt-3 font-serif font-light text-4xl sm:text-5xl text-forest leading-[0.95]">{title}</h1>
      </div>
      {children}
    </div>
  );
}

export function Badge({ children, tone = "muted" }) {
  const cls = { muted: "bg-linen text-charcoal/60", sage: "bg-sage/30 text-forest", champagne: "bg-champagne/25 text-forest", forest: "bg-forest text-ivory", red: "bg-destructive/10 text-destructive" }[tone];
  return <span className={`inline-block px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] ${cls}`}>{children}</span>;
}
